import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import { log } from '~/utils/logging';

import {
  DATA_SUBSCRIPTION_DIFFERENT_VALUES,
  DATA_SUBSCRIPTION_EVENT,
  DATA_SUBSCRIPTION_FILTER_CLAUSE_FILTER_KEYWORD,
  DATA_SUBSCRIPTION_FILTER_UNION,
  DATA_SUBSCRIPTION_TRIGGER,
  type FilterKey,
  type FilterUnionKey,
} from './constants';
import { DataSubscriptionDateFilterClauseObject } from './dataSubscriptionDateFilterClauseUtils';
import { DataSubscriptionFilterClauseObject } from './dataSubscriptionFilterClause';
import { type DataSubscription } from './types';

export const DataSubscriptionObject = {
  /**
   * Creates a new DataSubscription object.
   * @param {Partial<DataSubscription>} dataSubscription - The data for initializing the subscription.
   * @returns {DataSubscription} The initialized subscription object.
   */
  create(dataSubscription: Partial<DataSubscription> = {}) {
    return {
      active: dataSubscription?.isActive ?? true,
      companyAccountId: dataSubscription?.companyAccountId ?? '',
      dateFilterClauses:
        dataSubscription?.dateFilterClauses?.map((dateFilterClause) =>
          DataSubscriptionDateFilterClauseObject.create(dateFilterClause),
        ) ?? [],
      filterClauses:
        dataSubscription?.filterClauses?.map((filterClause) =>
          DataSubscriptionFilterClauseObject.create(filterClause),
        ) ?? [],
      filterUnion:
        dataSubscription?.filterUnion ?? DATA_SUBSCRIPTION_FILTER_UNION.AND.KEY,
      id: dataSubscription?.id ?? '',
      name: dataSubscription?.name ?? '',
      subscriptionEvents: dataSubscription?.subscriptionEvents ?? [],
      trigger:
        dataSubscription?.trigger ?? this.TRIGGER.PROCESS_STATE_CHANGE.KEY,
    };
  },
  FILTER_UNION: DATA_SUBSCRIPTION_FILTER_UNION,
  /**
   * Compares two DataSubscription objects and returns the differences.
   * @param {DataSubscription} dataSubscriptionA - The first subscription.
   * @param {DataSubscription} dataSubscriptionB - The second subscription.
   * @returns {string[]} An array of strings representing the differing properties.
   */
  getDifferences(
    dataSubscriptionA: Record<string, any>,
    dataSubscriptionB: Record<string, any>,
  ) {
    const differentValues = [];

    if (dataSubscriptionA?.id !== dataSubscriptionB?.id) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.ID);
    }

    if (
      dataSubscriptionA?.companyAccountId !==
      dataSubscriptionB?.companyAccountId
    ) {
      differentValues.push(
        DATA_SUBSCRIPTION_DIFFERENT_VALUES.COMPANY_ACCOUNT_ID,
      );
    }

    if (dataSubscriptionA?.name !== dataSubscriptionB?.name) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.NAME);
    }

    if (dataSubscriptionA?.trigger !== dataSubscriptionB?.trigger) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.TRIGGER);
    }

    if (
      JSON.stringify(dataSubscriptionA?.subscriptionEvents) !==
      JSON.stringify(dataSubscriptionB?.subscriptionEvents)
    ) {
      differentValues.push(
        DATA_SUBSCRIPTION_DIFFERENT_VALUES.SUBSCRIPTION_EVENTS,
      );
    }

    if (dataSubscriptionA?.active !== dataSubscriptionB?.active) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.ACTIVE);
    }

    if (dataSubscriptionA?.filterUnion !== dataSubscriptionB?.filterUnion) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.FILTER_UNION);
    }

    if (
      JSON.stringify(dataSubscriptionA?.filterClauses) !==
      JSON.stringify(dataSubscriptionB?.filterClauses)
    ) {
      differentValues.push(DATA_SUBSCRIPTION_DIFFERENT_VALUES.FILTER_CLAUSES);
    }

    if (
      JSON.stringify(dataSubscriptionA?.dateFilterClauses) !==
      JSON.stringify(dataSubscriptionB?.dateFilterClauses)
    ) {
      differentValues.push(
        DATA_SUBSCRIPTION_DIFFERENT_VALUES.DATE_FILTER_CLAUSES,
      );
    }

    return differentValues;
  },
  getFilterKeywordStringByKey(key: FilterKey) {
    const entry = Object.values(
      DATA_SUBSCRIPTION_FILTER_CLAUSE_FILTER_KEYWORD,
    ).find((item) => item.KEY === key);
    return entry?.STRING;
  },
  getFilterUnionStringByKey(key: FilterUnionKey) {
    const entry = Object.values(DATA_SUBSCRIPTION_FILTER_UNION).find(
      (item) => item.KEY === key,
    );
    return entry?.STRING;
  },
  getSubscriptionEventString(key: string) {
    const entry = Object.values(DATA_SUBSCRIPTION_EVENT).find(
      (item) => item.KEY === key,
    );

    if (!entry) {
      log.error({
        message: `Invalid subscription event: ${key}`,
        error: new EnumValueNotFoundException(
          `Invalid subscription event: ${key}`,
        ),
      });
      return key;
    }

    return entry.STRING;
  },
  SUBSCRIPTION_EVENT: DATA_SUBSCRIPTION_EVENT,
  TRIGGER: DATA_SUBSCRIPTION_TRIGGER,
};
